import type { TableData, TableItemType } from "../tableContext/TableContext";

type SelectionId = string | number;

export interface SelectedTableItem {
  id: SelectionId;
  [key: string]: unknown;
}

export const getTableSelectionId = (
  rowId: SelectionId,
  checkbox?: TableItemType["checkbox"],
) => checkbox?.id ?? rowId;

/** Only visible checkboxes on this page participate in select-all. */
export function getSelectableTableItems(
  rows: TableData[],
): SelectedTableItem[] {
  const items = new Map<SelectionId, SelectedTableItem>();
  rows.forEach((row) => {
    row.data.forEach(({ checkbox }) => {
      if (!checkbox || checkbox.isHidden) return;
      const id = getTableSelectionId(row.id, checkbox);
      if (!items.has(id)) items.set(id, { ...row.crudData, id });
    });
  });
  return Array.from(items.values());
}

export function toggleTableItemSelection(
  selected: SelectedTableItem[],
  id: SelectionId,
  crudData?: object,
): SelectedTableItem[] {
  return selected.some((item) => item.id === id)
    ? selected.filter((item) => item.id !== id)
    : [...selected, { ...crudData, id }];
}

export function getTableSelectionState(
  selected: SelectedTableItem[],
  rows: TableData[],
) {
  const selectable = getSelectableTableItems(rows);
  const selectedIds = new Set(selected.map((item) => item.id));
  const selectedCount = selectable.filter((item) =>
    selectedIds.has(item.id),
  ).length;
  return {
    disabled: selectable.length === 0,
    checked: selectable.length > 0 && selectedCount === selectable.length,
    indeterminate: selectedCount > 0 && selectedCount < selectable.length,
  };
}

export function toggleTableSelection(
  selected: SelectedTableItem[],
  rows: TableData[],
): SelectedTableItem[] {
  const selectable = getSelectableTableItems(rows);
  if (!selectable.length) return selected;
  const pageIds = new Set(selectable.map((item) => item.id));
  const selectedIds = new Set(selected.map((item) => item.id));
  const otherPages = selected.filter((item) => !pageIds.has(item.id));
  return selectable.every((item) => selectedIds.has(item.id))
    ? otherPages
    : [...otherPages, ...selectable];
}

export function getTableDataWithSelection(
  rows: TableData[],
  selected: SelectedTableItem[],
): TableData[] {
  const selectedIds = new Set(selected.map((item) => item.id));
  return rows.map((row) => ({
    ...row,
    data: row.data.map((cell) =>
      cell.checkbox
        ? {
            ...cell,
            checkbox: {
              ...cell.checkbox,
              isSelected: selectedIds.has(
                getTableSelectionId(row.id, cell.checkbox),
              ),
            },
          }
        : cell,
    ),
  }));
}
